import React, { useEffect, useState } from "react";
import { Button } from "../../../components/ui/button";
import { Progress } from "../../../components/ui/progress";
import { useNavigate, useParams } from "react-router-dom";
import { useUserContext } from "../../../Contexts/UserProvider";
import CircularProgress from "@mui/material/CircularProgress";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "../../../components/ui/avatar"
import { ImageIcon, Home, Building2, CheckCircle2, Users, Package, Eye, Upload, ChevronRight, Clock, DollarSign, MapPin, MoreVertical, Calendar, Phone, Mail, Globe, FileText, Edit, Camera, Copy, ExternalLink } from "lucide-react"
import { Separator } from "../../../components/ui/separator"
import { Badge } from "../../../components/ui/badge"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuLabel } from "../../../components/ui/dropdown-menu"

const Dashboard = () => {
  const [isContentLoading, setContentLoding] = useState(false);
  const { tenant } = useUserContext();
  const [copied, setCopied] = useState(false)
  const navigate = useNavigate();
  const { id } = useParams();

  const [isEditMode, setIsEditMode] = useState(false)

  const formatDate = (dateString: string | Date) => {
    return `${new Date(dateString).getFullYear()}-${String(new Date(dateString).getMonth() + 1).padStart(2, '0')}-${String(new Date(dateString).getDate()).padStart(2, '0')}`;
  }

  const getIndustryName = (industryId: string) => {
    // This would typically come from an API call
    return "Industrial Equipment & Components"
  }

  const getFullAddress = () => {
    const parts = [tenant?.address, tenant?.city, tenant?.state, tenant?.postalCode].filter(Boolean)
    return parts.join(", ")
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const handleUpdateCompany = () => {
    navigate(`/app/${id}/detail-form`);
  };

  const handleInvite = () => {
    navigate(`/app/${id}/companies`);
  };

  const handleInviteUsers = () => {
    navigate(`/app/${id}/user-management`);
  };

  const handleRoute = (path) => {
    navigate(`/app/${id}/${path}`);
  }

  useEffect(() => {
    if (id === tenant?.code) {
      setContentLoding(true);
    }
  }, [tenant]);

  const tasks = [
    {
      id: "profile",
      title: "Profile completion (45%)",
      description: "Finish setting up your company to unlock full features and improve discoverability.",
      icon: CheckCircle2,
      completed: false,
      onClicked: () => handleRoute("detail-form"),
    },
    {
      id: "invite",
      title: "Invite users",
      description: "Invite teammates to help you manage your organization, settings and products.",
      icon: Users,
      completed: false,
      onClicked: () => handleRoute("user-management"),
    },
    {
      id: "catalog",
      title: "Create product catalog",
      description: "Group your offerings to keep your product listings organized.",
      icon: Package,
      completed: false,
      onClicked: () => handleRoute("products"),
    },
    {
      id: "partners",
      title: "Invite partners/re-sellers",
      description: "Lorem ipsum",
      icon: Eye,
      completed: false,
      onClicked: () => handleRoute("companies"),
    },
    {
      id: "publish",
      title: "Publish your company profile",
      description: "Lorem ipsum",
      icon: Upload,
      completed: false,
      onClicked: () => { },
    },
  ]

  return (
    <>
      {isContentLoading ? (
        <div className="flex-1">
          <div className="bg-gradient-to-br from-orange-50 to-yellow-50 dark-company-header-outter">
            {/* Header Section */}
            <div className="relative bg-gradient-to-r from-gray-600 to-gray-800 text-white dark-company-header-inner">
              <div className="absolute inset-0 bg-black/20" />
              <div className="relative px-8 py-12">
                <div className="flex justify-between items-start mb-8">
                  <div className="flex items-center space-x-6">
                    <div className="relative">
                      <Avatar className="w-24 h-24 border-4 border-white/20">
                        <AvatarImage src={tenant?.logo || "/placeholder.svg"} alt={tenant?.name} />
                        <AvatarFallback className="bg-white/10 text-white text-2xl font-bold">
                          {tenant?.name.substring(0, 2).toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      {/* <Button size="sm" variant="secondary" className="absolute -bottom-2 -right-2 rounded-full w-8 h-8 p-0" onClick={() => handleRoute("detail-form")}>
                        <Camera className="w-4 h-4" />
                      </Button> */}
                    </div>
                    <div>
                      <h1 className="text-4xl font-bold mb-2">{tenant?.name}</h1>
                      <p className="text-xl text-gray-200 mb-4"> {tenant?.industry}</p>
                      <p className="text-xl text-gray-200 mb-4"> {tenant?.companyType}</p>
                    </div>
                  </div>
                  <Button
                    variant="secondary"
                    onClick={() => handleRoute("detail-form")}
                    className="bg-white/10 border-white/20 text-white hover:bg-white/20 dark-button"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Edit Profile
                  </Button>
                </div>

                {/* Company Description */}
                <div className="max-w-4xl">
                  <p className="text-lg text-gray-100 leading-relaxed mb-6">{tenant?.description}</p>

                  {/* Company Stats */}
                  <div className="flex space-x-8 text-sm">
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-4 h-4" />
                      <span>
                        {tenant?.city}, {tenant?.state}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>Est. {formatDate(tenant?.fiscalYearStart)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
            {/* Company Information */}
            <div className="lg:col-span-2">
              <Card className="shadow-sm border-gray-200">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center justify-between text-lg">
                    <div className="flex items-center space-x-2">
                      <Building2 className="w-5 h-5 text-gray-600" />
                      <span>Company Information</span>
                    </div>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Legal Name */}
                    <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-blue-500">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Legal Name</label>
                      <p className="mt-1 text-sm text-gray-900 font-medium">
                        {tenant?.legalName || tenant?.name}
                      </p>
                    </div>

                    {/* Business Registration */}
                    <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-green-500">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Business Registration
                      </label>
                      <p className="mt-1 text-sm text-gray-900 font-medium">
                        {tenant?.businessRegNumber || "Not provided"}
                      </p>
                    </div>

                    {/* Industry ID */}
                    <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-purple-500">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Industry </label>
                      <p className="mt-1 text-sm text-gray-900 font-medium">{tenant?.industry}</p>
                    </div>

                    {/* Company Type ID */}
                    <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-orange-500">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Company Type
                      </label>
                      <p className="mt-1 text-sm text-gray-900 font-medium">
                        {tenant?.companyType}
                      </p>
                    </div>

                    {/* Address */}
                    <div className="md:col-span-2 bg-gray-50 rounded-lg p-3 border-l-4 border-indigo-500">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Address</label>
                      <p className="mt-1 text-sm text-gray-900 font-medium">{getFullAddress() || "Not provided"}</p>
                    </div>

                    {/* Currency & Time Zone Row */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Currency</label>
                      <div className="mt-1 flex items-center">
                        <DollarSign className="w-4 h-4 text-green-600 mr-1" />
                        <p className="text-sm text-green-800 font-semibold">{tenant?.currency || "USD"}</p>
                      </div>
                    </div>

                    {/* <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg p-3 border border-blue-200">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Time Zone</label>
                      <div className="mt-1 flex items-center">
                        <Clock className="w-4 h-4 text-blue-600 mr-1" />
                        <p className="text-sm text-blue-800 font-semibold">
                          {tenant?.timeZone?.split("/")[1] || "New_York"}
                        </p>
                      </div>
                    </div> */}

                    {/* Fiscal Year Row */}
                    <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg p-3 border border-orange-200">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Fiscal Year Start
                      </label>
                      <p className="mt-1 text-sm text-orange-800 font-semibold">
                        {formatDate(tenant?.fiscalYearStart)}
                      </p>
                    </div>

                    <div className="bg-gradient-to-r from-pink-50 to-rose-50 rounded-lg p-3 border border-pink-200">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Fiscal Year End
                      </label>
                      <p className="mt-1 text-sm text-pink-800 font-semibold">{formatDate(tenant?.fiscalYearEnd)}</p>
                    </div>

                    {/* Language & Discoverable Row */}
                    {/* <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-teal-500">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Default Language
                      </label>
                      <p className="mt-1 text-sm text-gray-900 font-semibold">
                        {tenant?.defaultLanguage?.toUpperCase() || "EN"}
                      </p>
                    </div> */}

                    <div className="bg-gray-50 rounded-lg p-3 border-l-4 border-yellow-500">
                      <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Discoverable</label>
                      <div className="mt-1">
                        <Badge variant={tenant?.isDiscoverable ? "default" : "secondary"} className="text-xs">
                          {tenant?.isDiscoverable ? "Yes" : "No"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <Card className="shadow-sm border-gray-200">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center space-x-2 text-lg">
                    <Phone className="w-5 h-5 text-gray-600" />
                    <span>Contact Information</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 space-y-4">
                  {/* Email */}
                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-purple-100 rounded-lg">
                          <Mail className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</label>
                          <p className="text-sm text-purple-700 font-medium">{tenant?.email || "Not provided"}</p>
                        </div>
                      </div>
                      {tenant?.email && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(tenant?.email)}
                          className="h-8 w-8 p-0 hover:bg-purple-100"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-green-100 rounded-lg">
                          <Phone className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Phone</label>
                          <p className="text-sm text-green-700 font-medium">{tenant?.telephone || "Not provided"}</p>
                        </div>
                      </div>
                      {tenant?.telephone && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(tenant?.telephone)}
                          className="h-8 w-8 p-0 hover:bg-green-100"
                        >
                          <Copy className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Website */}
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                          <Globe className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Website</label>
                          <p className="text-sm text-blue-700 font-medium">
                            {tenant?.website || "Not provided"}
                          </p>
                        </div>
                      </div>
                      <Button size="sm" variant="ghost" className="h-8 w-8 p-0 hover:bg-blue-100">
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Billing Contact */}
              <Card className="shadow-sm border-gray-200 billing-contact">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg">Billing Contact</CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex items-center space-x-3 mb-3">
                      <Avatar className="w-10 h-10 border-2 border-white shadow-sm">
                        <AvatarFallback className="bg-indigo-100 text-indigo-600 font-semibold text-sm">
                          {tenant?.billingContactName
                            ?.split(" ")
                            .map((n) => n[0])
                            .join("") || "BC"}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold text-gray-900 text-sm">
                          {tenant?.billingContactName || "Not provided"}
                        </p>
                        <p className="text-xs text-gray-500">Billing Manager</p>
                      </div>
                    </div>

                    <div className="space-y-2">
                      {tenant?.billingContactEmail && (
                        <div className="flex items-center space-x-2">
                          <Mail className="w-3 h-3 text-gray-400" />
                          <p className="text-sm text-gray-600">{tenant?.billingContactEmail}</p>
                        </div>
                      )}

                      {tenant?.billingContactPhone && (
                        <div className="flex items-center space-x-2">
                          <Phone className="w-3 h-3 text-gray-400" />
                          <p className="text-sm text-gray-600">{tenant?.billingContactPhone}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center h-screen">
          <CircularProgress />
        </div>
      )}
    </>
  );
};

export default Dashboard;
